/* =========================================================================
   ClueVine — Trellis (word ladder)
   Climb from a start word to a target, changing one letter per rung, every rung
   a real word. Puzzles are generated by breadth-first search over the
   one-letter-change graph, so a solution always exists; hints walk the shortest
   remaining path. Thematic: a vine climbing a trellis.
   ========================================================================= */

import { pickAvoiding } from '../../engine/seed';
import type {
  Dictionary,
  DifficultyLevel,
  GenerateContext,
  HintOutcome,
  InputKey,
  ModeScore,
  PuzzleStatus,
  ResultSummary,
} from '../../engine/types';

export interface TrellisConfig {
  length: number;
  steps: number;
}

export interface TrellisState {
  start: string;
  target: string;
  length: number;
  optimal: number; // shortest number of rungs
  maxRungs: number; // budget before a loss
  path: string[]; // committed words, path[0] === start
  current: string;
}

export type TrellisMove =
  | { type: 'add'; ch: string }
  | { type: 'remove' }
  | { type: 'submit' };

export const TRELLIS_DIFFICULTIES: DifficultyLevel<TrellisConfig>[] = [
  { id: 'easy', label: 'Sprout', blurb: '4 letters · 3 rungs', scoreMultiplier: 0.9, config: { length: 4, steps: 3 } },
  { id: 'standard', label: 'Garden', blurb: '4 letters · 4 rungs', scoreMultiplier: 1, config: { length: 4, steps: 4 } },
  { id: 'hard', label: 'Thicket', blurb: '5 letters · 3 rungs', scoreMultiplier: 1.35, config: { length: 5, steps: 3 } },
];

const A = 'a'.charCodeAt(0);

/** Words one letter-change away that exist in the dictionary. */
export function neighbors(word: string, dict: Dictionary): string[] {
  const out: string[] = [];
  for (let i = 0; i < word.length; i++) {
    for (let c = 0; c < 26; c++) {
      const ch = String.fromCharCode(A + c);
      if (ch === word[i]) continue;
      const candidate = word.slice(0, i) + ch + word.slice(i + 1);
      if (dict.isValid(candidate)) out.push(candidate);
    }
  }
  return out;
}

export function differsByOne(a: string, b: string): boolean {
  if (a.length !== b.length) return false;
  let diff = 0;
  for (let i = 0; i < a.length; i++) {
    if (a[i] !== b[i]) diff += 1;
    if (diff > 1) return false;
  }
  return diff === 1;
}

interface Bfs {
  dist: Map<string, number>;
  parent: Map<string, string>;
}

function bfs(dict: Dictionary, start: string, maxDepth: number, nodeCap: number): Bfs {
  const dist = new Map<string, number>([[start, 0]]);
  const parent = new Map<string, string>();
  const queue = [start];
  let head = 0;
  while (head < queue.length && dist.size < nodeCap) {
    const word = queue[head++];
    const d = dist.get(word)!;
    if (d >= maxDepth) continue;
    for (const nb of neighbors(word, dict)) {
      if (!dist.has(nb)) {
        dist.set(nb, d + 1);
        parent.set(nb, word);
        queue.push(nb);
      }
    }
  }
  return { dist, parent };
}

function reconstruct(parent: Map<string, string>, start: string, target: string): string[] {
  const path = [target];
  let w = target;
  while (w !== start) {
    const p = parent.get(w);
    if (p == null) break;
    path.push(p);
    w = p;
  }
  return path.reverse();
}

/** Shortest path of words from `from` to `target` (inclusive), or null. */
export function shortestPath(
  dict: Dictionary,
  from: string,
  target: string,
  nodeCap = 8000,
): string[] | null {
  if (from === target) return [from];
  const dist = new Map<string, number>([[from, 0]]);
  const parent = new Map<string, string>();
  const queue = [from];
  let head = 0;
  while (head < queue.length && dist.size < nodeCap) {
    const word = queue[head++];
    for (const nb of neighbors(word, dict)) {
      if (!dist.has(nb)) {
        dist.set(nb, dist.get(word)! + 1);
        parent.set(nb, word);
        if (nb === target) return reconstruct(parent, from, target);
        queue.push(nb);
      }
    }
  }
  return null;
}

export function generate(ctx: GenerateContext<TrellisConfig>): TrellisState {
  const { length, steps } = ctx.difficulty.config;
  const pool = ctx.dictionary.answersByLength(length);
  const answerSet = new Set(pool);

  let start = pickAvoiding(ctx.prng, pool, ctx.avoid);
  let solution: string[] | null = null;

  for (let attempt = 0; attempt < 10 && !solution; attempt++) {
    start = attempt === 0 ? start : ctx.prng.pick(pool);
    const { dist, parent } = bfs(ctx.dictionary, start, steps, 6000);
    const atDepth = [...dist.entries()].filter(([, d]) => d === steps).map(([w]) => w);
    if (atDepth.length > 0) {
      const common = atDepth.filter((w) => answerSet.has(w));
      const target = ctx.prng.pick(common.length > 0 ? common : atDepth);
      solution = reconstruct(parent, start, target);
    }
  }

  if (!solution) {
    // Fallback: take the deepest reachable node (guarantees solvability).
    const { dist, parent } = bfs(ctx.dictionary, start, steps, 6000);
    let best = start;
    let bestD = 0;
    for (const [w, d] of dist) {
      if (d > bestD) {
        best = w;
        bestD = d;
      }
    }
    solution = bestD > 0 ? reconstruct(parent, start, best) : [start, ctx.prng.pick(neighbors(start, ctx.dictionary))];
  }

  const target = solution[solution.length - 1];
  const optimal = solution.length - 1;
  return {
    start,
    target,
    length,
    optimal,
    maxRungs: optimal + 2,
    path: [start],
    current: '',
  };
}

export function status(state: TrellisState): PuzzleStatus {
  const last = state.path[state.path.length - 1];
  if (last === state.target) return 'won';
  if (state.path.length - 1 >= state.maxRungs) return 'lost';
  return 'playing';
}

export function validateMove(state: TrellisState, move: TrellisMove): { ok: boolean; reason?: string } {
  if (move.type !== 'submit') return { ok: true };
  const last = state.path[state.path.length - 1];
  if (state.current.length < state.length) return { ok: false, reason: 'Not enough letters' };
  if (!differsByOne(state.current, last)) return { ok: false, reason: 'Change exactly one letter' };
  if (state.path.includes(state.current)) return { ok: false, reason: 'Already used' };
  // dictionary validity is checked in the mode wrapper
  return { ok: true };
}

export function reduce(state: TrellisState, move: TrellisMove): TrellisState {
  switch (move.type) {
    case 'add':
      if (state.current.length >= state.length) return state;
      return { ...state, current: state.current + move.ch };
    case 'remove':
      return state.current ? { ...state, current: state.current.slice(0, -1) } : state;
    case 'submit': {
      const last = state.path[state.path.length - 1];
      if (state.current.length < state.length || !differsByOne(state.current, last)) return state;
      if (state.path.includes(state.current)) return state;
      return { ...state, path: [...state.path, state.current], current: '' };
    }
    default:
      return state;
  }
}

export function handleKey(state: TrellisState, key: InputKey): TrellisMove | null {
  if (key.type === 'char') {
    return state.current.length < state.length ? { type: 'add', ch: key.value } : null;
  }
  if (key.type === 'backspace') {
    return state.current ? { type: 'remove' } : null;
  }
  return { type: 'submit' };
}

export function changedIndex(prev: string, word: string): number {
  for (let i = 0; i < word.length; i++) if (prev[i] !== word[i]) return i;
  return -1;
}

export function makeHint(state: TrellisState, dict: Dictionary): HintOutcome<TrellisState> | null {
  if (status(state) !== 'playing') return null;
  const last = state.path[state.path.length - 1];
  const path = shortestPath(dict, last, state.target);
  if (!path || path.length < 2) return null;
  const next = path[1];
  const pos = changedIndex(last, next);
  return {
    message: `Change position ${pos + 1} to “${next[pos].toUpperCase()}”.`,
    penalty: 50,
  };
}

export function score(state: TrellisState): ModeScore {
  if (status(state) !== 'won') {
    return { base: 0, lines: [{ label: 'Not solved', value: 0, kind: 'base' }], parMs: state.length * 9000 };
  }
  const rungs = state.path.length - 1;
  const base = 220 + (state.maxRungs - rungs) * 130 + state.length * 30;
  return {
    base,
    lines: [{ label: `Climbed in ${rungs} rungs`, value: base, kind: 'base' }],
    parMs: state.length * 9000,
    perfect: rungs === state.optimal,
  };
}

export function summarize(state: TrellisState): ResultSummary {
  const won = status(state) === 'won';
  return {
    status: won ? 'won' : 'lost',
    headline: won ? `Reached ${state.target.toUpperCase()} in ${state.path.length - 1}` : 'Out of rungs',
    answer: `${state.start.toUpperCase()} → ${state.target.toUpperCase()}`,
    revealLabel: 'Ladder',
    avoidKeys: [state.start],
    detail: won ? state.path.map((w) => w.toUpperCase()).join(' → ') : undefined,
    shareGrid: [won ? `🪜 ${state.path.length - 1} rungs` : '🥀 stuck'],
  };
}

export function deserialize(raw: unknown): TrellisState | null {
  if (!raw || typeof raw !== 'object') return null;
  const s = raw as Partial<TrellisState>;
  if (typeof s.start !== 'string' || typeof s.target !== 'string' || !Array.isArray(s.path)) return null;
  if (typeof s.current !== 'string' || typeof s.length !== 'number') return null;
  return {
    start: s.start,
    target: s.target,
    length: s.length,
    optimal: typeof s.optimal === 'number' ? s.optimal : s.path.length,
    maxRungs: typeof s.maxRungs === 'number' ? s.maxRungs : (s.optimal ?? 4) + 2,
    path: s.path.filter((w): w is string => typeof w === 'string'),
    current: s.current,
  };
}
